import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useContext } from 'react';

import { TimesyncContext } from '../providers/TimesyncProvider/TimesyncProvider';
import BackendService from '../services/backendService';
import {
  Template,
  TemplateEditOrCreateDTO,
} from '../services/TemplateService/Template.type';
import { templateCacheKey } from '../utils/blueprintUtils';
import { buildTemplateResponseFromTemplateEditOrCreateDTO } from '../utils/templateAutosaveUtils';
import { NEW_TEMPLATE_PARAM } from '../utils/urlUtils';
import { resultsCacheKey } from './useGames';
import { useMetrics } from './useMetrics';
import { presentationsCacheKey } from './usePresentations';
import useRouterMatch from './useRouterMatch';
import { templatesCacheKey } from './useTemplates';
import { workspaceTemplatesCacheKey } from './useWorkspaceTemplates';

function useAutosaveTemplate() {
  const client = useQueryClient();
  const { tenantId, router } = useRouterMatch();
  const metrics = useMetrics();
  const { now } = useContext(TimesyncContext);

  const {
    mutate: autoSaveTemplate,
    isPending: isSavingTemplate,
    isError: isSavingTemplateError,
    isSuccess: isSavingTemplateSuccess,
    isIdle: isSavingTemplateIdle,
  } = useMutation({
    mutationFn: (config: {
      templateConfig?: TemplateEditOrCreateDTO | null;
      onSuccessFunction?: (data: Template) => void | Promise<void> | undefined;
    }) => {
      if (!config.templateConfig) return Promise.resolve(undefined);
      //updated the modifiedAt time using the offset correction
      const timeSyncedTemplateConfig = {
        ...config.templateConfig,
        modifiedAt: now(),
      };

      return BackendService.instance.template.templateAutoSave(
        timeSyncedTemplateConfig,
        tenantId,
      );
    },
    retryDelay: 2000,
    onSuccess: (data, { onSuccessFunction, templateConfig }) => {
      if (!data) return;

      if (templateConfig && templateConfig?.id === NEW_TEMPLATE_PARAM && data.id) {
        metrics.logEvent('Template.New');
        // Make sure we only set the cache and redirect when we save a new template without an ID
        client.setQueryData<Template | undefined>(
          [templateCacheKey, data.id],
          (exitingsTemplate: Template | undefined) => {
            if (!exitingsTemplate) return data;
            if (exitingsTemplate.modifiedAt < data.modifiedAt) return data;
            return { ...exitingsTemplate, id: data.id };
          },
        );

        // Clear the old [templateId, "new"] react query cache
        // templateConfig.id is "new" at this line
        client.setQueryData([templateCacheKey, templateConfig.id], () => undefined);

        router.replace(`/${tenantId}/lessons/${data.id}/edit`);
        /* After this happens - useTemplate in the creator will return the latest cache, 
            and we will sync template into localTemplate to prevent infinite saves
          */
      }
      if (templateConfig?.parentId) {
        // invalidate folder presentation cache if this lesson was created there.
        client.invalidateQueries({
          queryKey: [
            presentationsCacheKey,
            templateConfig.isPrivate,
            templateConfig.parentId,
          ],
        });
      }
      client.invalidateQueries({
        queryKey: [templatesCacheKey],
      });
      client.invalidateQueries({
        queryKey: [templateConfig?.id, 'presentationVersion'],
      });
      if (onSuccessFunction) {
        onSuccessFunction(data);
      }
    },
    retry: (count: number, error: AxiosError) => {
      // due to SSR fetching we dont want to retry on 403s and 404s
      if (error.response?.status && [403, 404].includes(error.response?.status)) {
        return false;
      }

      // Default retry is 3 times
      if (count > 3) {
        return false;
      }
      return true;
    },
    onMutate: async mutateBody => {
      const templateCacheKey = ['template', mutateBody.templateConfig?.id];

      await client.cancelQueries({
        queryKey: templateCacheKey,
      });
      const previousTemplate = client.getQueryData<Template>(templateCacheKey);
      client.setQueryData<Template | undefined>(templateCacheKey, template => {
        if (!template || !mutateBody.templateConfig) return template;
        return buildTemplateResponseFromTemplateEditOrCreateDTO(
          mutateBody.templateConfig,
          template,
        );
      });

      // updates the title in the templates list query
      if (mutateBody.templateConfig?.title && previousTemplate) {
        // title for my lesson listing
        if (previousTemplate?.isPrivate) {
          const cacheKey = previousTemplate.parentId
            ? [templatesCacheKey, previousTemplate.parentId]
            : [templatesCacheKey];
          client.invalidateQueries({ queryKey: cacheKey });
          client.invalidateQueries({
            queryKey: [presentationsCacheKey, true],
          });
        }

        // title for workspace listing
        if (!previousTemplate?.isPrivate) {
          const cacheKey = previousTemplate.parentId
            ? [workspaceTemplatesCacheKey, previousTemplate.parentId]
            : [workspaceTemplatesCacheKey];
          client.invalidateQueries({ queryKey: cacheKey });
          //Invalidate the new workspace cache query
          client.invalidateQueries({
            queryKey: [presentationsCacheKey, false],
          });
        }
      }

      // invalidate presentations cache when activeGame or gameIds are updated
      if (
        (mutateBody.templateConfig?.activeGame !== undefined ||
          mutateBody.templateConfig?.gameIds !== undefined) &&
        previousTemplate
      ) {
        // invalidate private presentations cache
        if (previousTemplate?.isPrivate) {
          client.invalidateQueries({
            queryKey: [presentationsCacheKey, true],
          });
        }

        // invalidate workspace presentations cache
        if (!previousTemplate?.isPrivate) {
          client.invalidateQueries({
            queryKey: [presentationsCacheKey, false],
          });
        }
      }

      // invalidate the query for listing games related to the template
      if (mutateBody.templateConfig?.gameIds) {
        const cacheKey = [resultsCacheKey, mutateBody.templateConfig.id];
        client.invalidateQueries({ queryKey: cacheKey });
      }

      // Return the snapshotted value
      return () => client.setQueryData(templateCacheKey, previousTemplate);
    },
  });

  return {
    autoSaveTemplate,
    isSavingTemplate,
    isSavingTemplateError,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
  };
}
export default useAutosaveTemplate;
